import type { Package } from "@revenuecat/purchases-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevenueCat } from "@/hooks/use-revenuecat";
import { MONTHLY_PLANS } from "@/lib/revenuecat";
import type { MonthlyPlan } from "@/lib/revenuecat";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Crown,
  ExternalLink,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";

function formatDate(date: Date | null) {
  if (!date) return "—";
  return date.toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" });
}

interface RevenueCatSubscriptionCardProps {
  appUserId: string | undefined;
  customerEmail?: string;
}

export function RevenueCatSubscriptionCard({ appUserId, customerEmail }: RevenueCatSubscriptionCardProps) {
  const {
    customerInfo,
    activePlan,
    currentOffering,
    isLoading,
    error,
    presentPaywall,
    purchasePackage,
    isPurchasing,
    refresh,
  } = useRevenueCat(appUserId, customerEmail);

  // Cada plan con su paquete del offering; se omiten los que no existan en RevenueCat.
  const monthlyPlans = MONTHLY_PLANS.map((plan) => ({
    plan,
    pkg: currentOffering?.packagesById[plan.packageId],
  })).filter((item): item is { plan: MonthlyPlan; pkg: Package } => !!item.pkg);

  const entitlement = activePlan?.entitlement;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-primary" />
            Planes mensuales
          </span>
          {activePlan ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-emerald-500/15 text-emerald-500 border-emerald-500/30">
              <CheckCircle2 className="w-3 h-3" />
              {activePlan.plan.name} activo
            </span>
          ) : (
            appUserId && !isLoading && !error && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border bg-muted text-muted-foreground border-border">
                Sin suscripción
              </span>
            )
          )}
        </CardTitle>
      </CardHeader>

      <CardContent>
        {!appUserId ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No se pudo identificar la cuenta para consultar la suscripción.
          </p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando planes...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <AlertTriangle className="w-8 h-8 text-destructive/60" />
            <p className="text-sm text-muted-foreground">No se pudo cargar la información de suscripción.</p>
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Reintentar
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Detalle de la suscripción activa */}
            {entitlement && (
              <>
                <div className="space-y-3">
                  <Row label="Activo desde" value={formatDate(entitlement.originalPurchaseDate)} />
                  <Separator />
                  <Row
                    label={entitlement.willRenew ? "Próxima renovación" : "Expira el"}
                    value={formatDate(entitlement.expirationDate)}
                  />
                </div>

                {!entitlement.willRenew && (
                  <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                    <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                    La renovación automática está desactivada. Conservarás el acceso hasta la fecha de expiración.
                  </div>
                )}

                {customerInfo?.managementURL && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={customerInfo.managementURL} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Gestionar suscripción
                    </a>
                  </Button>
                )}
                <Separator />
              </>
            )}

            {/* Listado de planes mensuales */}
            {monthlyPlans.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay planes disponibles en este momento.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {monthlyPlans.map(({ plan, pkg }) => {
                  const isCurrent = activePlan?.plan.packageId === plan.packageId;
                  return (
                    <div
                      key={pkg.identifier}
                      className={`flex flex-col rounded-lg border px-4 py-4 ${
                        isCurrent ? "border-primary/50 bg-primary/5" : "border-border bg-muted/30"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">{plan.name}</p>
                        {isCurrent && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                            Plan actual
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-2xl font-bold text-foreground">
                        {pkg.product.price.formattedPrice}
                        <span className="ml-1 text-xs font-normal text-muted-foreground">/ mes</span>
                      </p>

                      {plan.features.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-px shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Con una suscripción activa, los cambios de plan se hacen desde el portal */}
                      {!activePlan && (
                        <Button
                          size="sm"
                          className="mt-4"
                          disabled={isPurchasing}
                          onClick={() => purchasePackage(pkg)}
                        >
                          {isPurchasing && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                          Suscribirme
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!activePlan && currentOffering?.hasPaywall && (
              <Button variant="ghost" size="sm" onClick={() => presentPaywall()} disabled={isPurchasing}>
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Ver todas las opciones
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value}</span>
    </div>
  );
}
